/**
 * VoyagerSearchService - Service for interacting with the ODN Voyager Search API
 *
 * This service handles search requests to the Voyager Solr-based search API,
 * providing search results, facets, and heatmap data for geospatial visualization.
 */
import { logDebug, logWarn } from '../../../../utils/logger';
import type {
  ResultListState,
  ResultListStateQuery,
} from '../state/state.types';
import type {
  FilterSection,
  FilterItem,
  VoyagerDisplayConfig,
} from '../models/search-result';
import type {
  VoyagerApiConfig,
  VoyagerFacetConfig,
  VoyagerSearchResponse,
  VoyagerDocument,
  VoyagerFacetHeatmap,
  VoyagerFacetCounts,
  VoyagerHeatmapResponse,
} from './VoyagerSearchService.types';
import {
  DEFAULT_VOYAGER_CONFIG,
  getVoyagerSearchUrl,
  getVoyagerDisplayConfigUrl,
} from './defaultConfig';
import {
  stripFacetLocalParams,
  buildFacetDisplayNames,
  buildFacetMaxValues,
  buildFacetCategoryPaths,
  buildFacetShortLabelFlags,
  buildFacetValueCategories,
  buildFacetParams,
  getConfiguredFacetFieldOrder,
  shortenFacetValueLabel,
} from './facetHelpers';
import { groupFacetValues, buildTreeNsdiHierarchyView } from './treeHelpers';
import { buildHeatmapUrl, getHeatmapFromResponse } from './heatmapHelpers';
import { buildSearchUrl } from './searchUrlBuilder';
import { transformResponse } from './voyagerDocumentTransform';

// Re-export types and config for consumers that already import from this file
export type {
  VoyagerApiConfig,
  VoyagerFacetFieldConfig,
  VoyagerFacetValueCategoryConfig,
  VoyagerFacetParamConfig,
  VoyagerFacetConfig,
  VoyagerSearchResponse,
  VoyagerDocument,
  VoyagerFacetCounts,
  VoyagerHeatmapResponse,
} from './VoyagerSearchService.types';
export {
  DEFAULT_VOYAGER_CONFIG,
  DEFAULT_VOYAGER_FACET_FIELDS,
  DEFAULT_VOYAGER_FACET_PARAMS,
} from './defaultConfig';

// Fields to return from search results
export const RESULT_FIELDS = [
  'id',
  'title',
  'name:[name]',
  'format',
  'abstract',
  'fullpath:[absolute]',
  'absolute_path:[absolute]',
  'thumb:[thumbURL]',
  'path_to_thumb',
  'subject',
  'download:[downloadURL]',
  'format_type',
  'bytes',
  'modified',
  'shard:[shard]',
  'bbox',
  'geo:[geo]',
  'format_category',
  'component_files',
  'ags_fused_cache',
  'linkcount__children',
  'contains_name',
  'wms_layer_name',
  'tag_flags',
  'tag_tags',
  'hasMissingData',
  'layerURL:[lyrURL]',
  'hasLayerFile',
  'likes',
  'dislikes',
  'views',
  'description',
  'keywords',
  'name',
  'name_alias',
  'fd_publish_date',
  'fs_event',
  'fd__acquired',
  'fs_platform',
  'fs__provider',
  'fd_acquired',
  'fs__satellite_id',
  'rasterSize',
  'format_app',
  'grp_Country',
].join(',');

const getFacetConfig = (): VoyagerFacetConfig => {
  const configuredFacetConfig = VoyagerSearchService.getConfig().facetConfig;

  return {
    ...DEFAULT_VOYAGER_CONFIG.facetConfig,
    ...configuredFacetConfig,
    fields: configuredFacetConfig?.fields?.length
      ? configuredFacetConfig.fields
      : DEFAULT_VOYAGER_CONFIG.facetConfig.fields,
    params: {
      ...(DEFAULT_VOYAGER_CONFIG.facetConfig.params || {}),
      ...(configuredFacetConfig?.params || {}),
    },
  };
};

/**
 * Voyager Search Service class for handling search operations
 */
export class VoyagerSearchService {
  // Current configuration
  private static config: VoyagerApiConfig = DEFAULT_VOYAGER_CONFIG;

  /**
   * Configure the Voyager Search Service with custom API settings
   */
  static configure(config: Partial<VoyagerApiConfig>): void {
    const filteredConfig = Object.fromEntries(
      Object.entries(config).filter(
        ([, v]) => v !== undefined && v !== null && v !== '',
      ),
    ) as Partial<VoyagerApiConfig>;
    VoyagerSearchService.config = {
      ...DEFAULT_VOYAGER_CONFIG,
      ...filteredConfig,
    };
    logDebug(
      'VoyagerSearchService: Configured with',
      VoyagerSearchService.config,
    );
  }

  /**
   * Get the current configuration
   */
  static getConfig(): VoyagerApiConfig {
    return { ...VoyagerSearchService.config };
  }

  /**
   * Get the search URL for the current config
   */
  private static getSearchUrl(): string {
    logDebug(
      'VoyagerSearchService: Current config',
      VoyagerSearchService.config,
    );
    return getVoyagerSearchUrl(VoyagerSearchService.config);
  }

  /**
   * Get the display config URL for the current config
   */
  private static getDisplayConfigUrl(): string {
    return getVoyagerDisplayConfigUrl(VoyagerSearchService.config);
  }

  /**
   * Get the config ID
   */
  private static getConfigId(): string {
    return VoyagerSearchService.config.configId;
  }

  /**
   * Execute a search query against the Voyager API
   */
  static async search(
    query: ResultListStateQuery = {},
    options: {
      includeFacets?: boolean;
      includeHeatmap?: boolean;
    } = { includeFacets: true, includeHeatmap: false },
  ): Promise<ResultListState> {
    try {
      const searchUrl = buildSearchUrl({
        query,
        options,
        searchUrl: VoyagerSearchService.getSearchUrl(),
        configId: VoyagerSearchService.getConfigId(),
        facetConfig: getFacetConfig(),
        resultFields: RESULT_FIELDS,
      });
      logDebug('VoyagerSearchService: Executing search', searchUrl);
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error(`Voyager API error: ${response.status}`);
      }

      const data: VoyagerSearchResponse = await response.json();
      return transformResponse(
        data,
        query,
        data.facet_counts
          ? VoyagerSearchService.transformFacets(data.facet_counts)
          : [],
      );
    } catch (error) {
      logWarn('VoyagerSearchService: Search failed', error);
      // Return empty results on error
      return {
        id: 'voyager-search',
        isLoading: false,
        query,
        results: {
          count: 0,
          data: [],
          filterSections: [],
          autocomplete: [],
        },
      };
    }
  }

  /**
   * Fetch facet counts only (for sidebar filters)
   */
  static async fetchFacets(): Promise<FilterSection[]> {
    try {
      const params = new URLSearchParams();
      params.set('basemap', 'ESRI Street Map');
      params.set('disp', VoyagerSearchService.getConfigId());
      params.set('view', 'card');
      params.set('filter', 'true');
      params.set('voyager.config.id', VoyagerSearchService.getConfigId());
      params.set('rows', '0');
      params.set('facet', 'true');
      params.set('facet.mincount', String(getFacetConfig().minCount || 1));
      params.set('facet.limit', String(getFacetConfig().limit || 11));
      params.set('wt', 'json');

      // Add facet fields
      getFacetConfig().fields.forEach(({ field }) => {
        params.append('facet.field', field);
      });

      Object.entries(buildFacetParams(getFacetConfig())).forEach(
        ([key, value]) => {
          params.set(key, value);
        },
      );

      const url = `${VoyagerSearchService.getSearchUrl()}?${params.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Voyager facet API error: ${response.status}`);
      }

      const data: VoyagerSearchResponse = await response.json();
      return VoyagerSearchService.transformFacets(data.facet_counts);
    } catch (error) {
      logWarn('VoyagerSearchService: Facet fetch failed', error);
      return [];
    }
  }

  /**
   * Fetch heatmap data for geographic visualization
   */
  static async fetchHeatmap(
    gridLevel = 2,
  ): Promise<VoyagerFacetHeatmap | null> {
    try {
      const url = buildHeatmapUrl(
        VoyagerSearchService.getSearchUrl(),
        VoyagerSearchService.getConfigId(),
        gridLevel,
      );
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Voyager heatmap API error: ${response.status}`);
      }

      const data: VoyagerHeatmapResponse = await response.json();
      return getHeatmapFromResponse(data);
    } catch (error) {
      logWarn('VoyagerSearchService: Heatmap fetch failed', error);
      return null;
    }
  }

  /**
   * Fetch the display configuration from Voyager API
   * This configuration defines how results are displayed, which fields to show, sorting options, etc.
   */
  static async fetchDisplayConfig(
    configId?: string,
  ): Promise<VoyagerDisplayConfig | null> {
    try {
      const id = configId || VoyagerSearchService.getConfigId();
      const url = `${VoyagerSearchService.getDisplayConfigUrl()}/${id}`;
      logDebug('VoyagerSearchService: Fetching display config', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Voyager display config API error: ${response.status}`);
      }

      const data: VoyagerDisplayConfig = await response.json();
      logDebug('VoyagerSearchService: Display config loaded', data.title);
      return data;
    } catch (error) {
      logWarn('VoyagerSearchService: Display config fetch failed', error);
      return null;
    }
  }

  /**
   * Fetch detailed information for a specific item by ID
   * Uses the Voyager API endpoint format with fq filter and full field list
   */
  /**
   * Fetch a single item.
   *
   * `configOverride` exists for the SSR prefetch (config/settings.ts), which
   * runs outside React and must query the Voyager instance belonging to the
   * page being rendered. It cannot call configure() to do that: the config
   * below is a module-level static shared by the whole Node process, so two
   * concurrent SSR requests for different agencies would race on it. Passing
   * the config per call keeps that path free of shared state; every other
   * caller is client-side and continues to use the configured static.
   */
  static async fetchItemDetails(
    itemId: string,
    configOverride?: Partial<VoyagerApiConfig>,
  ): Promise<VoyagerDocument | null> {
    const effectiveConfig: VoyagerApiConfig = configOverride
      ? {
          ...VoyagerSearchService.config,
          ...(Object.fromEntries(
            Object.entries(configOverride).filter(
              ([, v]) => v !== undefined && v !== null && v !== '',
            ),
          ) as Partial<VoyagerApiConfig>),
        }
      : VoyagerSearchService.config;

    try {
      // Build query using fq filter (exact match) as per Voyager API spec
      const params = new URLSearchParams();
      params.set('fq', `id:${itemId}`);
      // Full field list with computed fields matching the Voyager API format
      params.set(
        'fl',
        [
          'id',
          'name:[name]',
          'fullpath:[absolute]',
          'absolute_path:[absolute]',
          'content:[contentURL]',
          'thumb:[thumbURL]',
          'preview:[previewURL]',
          'download:[downloadURL]',
          'serviceUrl:[proxyUrl]',
          'bbox',
          'format',
          'hasMetadata',
          'root',
          'tree',
          'tag_tags',
          'links',
          'geo:[geo]',
          'geo_full:[geo field=geo_full]',
          'hasMissingData',
          'schema',
          'layerURL:[lyrURL]',
          'likes',
          'dislikes',
          '*',
        ].join(','),
      );
      params.set('shards.info', 'true');
      params.set('shards.tolerant', 'true');
      params.set('wt', 'json');
      params.set('block', 'false');
      params.set('rand', String(Math.random()));
      params.set('disp', effectiveConfig.configId);
      params.set('voyager.config.id', effectiveConfig.configId);

      const url = `${getVoyagerSearchUrl(effectiveConfig)}?${params.toString()}`;
      logDebug('VoyagerSearchService: Fetching item details', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Voyager item details API error: ${response.status}`);
      }

      const data: VoyagerSearchResponse = await response.json();

      if (data.response.docs && data.response.docs.length > 0) {
        return data.response.docs[0];
      }

      logWarn('VoyagerSearchService: Item not found', itemId);
      return null;
    } catch (error) {
      logWarn('VoyagerSearchService: Item details fetch failed', error);
      return null;
    }
  }

  /**
   * Submit a vote (like/dislike) for an item
   */
  static async submitVote(
    itemId: string,
    voteType: 'up' | 'down',
  ): Promise<boolean> {
    try {
      // Note: This is a placeholder - actual implementation depends on Voyager API
      const endpoint = voteType === 'up' ? 'like' : 'dislike';
      const url = `${VoyagerSearchService.config.baseUrl}/api/rest/rating/${endpoint}/${itemId}`;

      logDebug('VoyagerSearchService: Submitting vote', { itemId, voteType });

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
      });

      return response.ok;
    } catch (error) {
      logWarn('VoyagerSearchService: Vote submission failed', error);
      return false;
    }
  }

  /**
   * Transform Voyager facet counts to FilterSection format
   */
  private static transformFacets(
    facetCounts: VoyagerFacetCounts | undefined,
  ): FilterSection[] {
    if (!facetCounts?.facet_fields) {
      return [];
    }

    const facetConfig = getFacetConfig();
    const configuredFieldOrder = getConfiguredFacetFieldOrder(facetConfig);
    const configuredDisplayNames = buildFacetDisplayNames(facetConfig);
    const configuredMaxValues = buildFacetMaxValues(facetConfig);
    const configuredCategoryPaths = buildFacetCategoryPaths(facetConfig);
    const configuredShortLabelFlags = buildFacetShortLabelFlags(facetConfig);
    const configuredValueCategories = buildFacetValueCategories(facetConfig);
    const sections: FilterSection[] = [];

    const orderedFieldEntries = [
      ...configuredFieldOrder
        .filter((fieldName) =>
          Object.prototype.hasOwnProperty.call(
            facetCounts.facet_fields,
            fieldName,
          ),
        )
        .map(
          (fieldName) =>
            [fieldName, facetCounts.facet_fields[fieldName]] as const,
        ),
      ...Object.entries(facetCounts.facet_fields).filter(
        ([fieldName]) => !configuredFieldOrder.includes(fieldName),
      ),
    ];

    orderedFieldEntries.forEach(([fieldName, values]) => {
      const cleanFieldName = stripFacetLocalParams(fieldName);

      // Skip empty facets
      if (!values || values.length === 0) {
        return;
      }

      // Parse facet values (alternating name/count pairs)
      const filters: FilterItem[] = [];
      for (let i = 0; i < values.length; i += 2) {
        const rawValue = String(values[i]);
        const count = Number(values[i + 1]);

        if (count > 0) {
          filters.push({
            sectionId: cleanFieldName,
            id: rawValue,
            label: configuredShortLabelFlags[cleanFieldName]
              ? shortenFacetValueLabel(rawValue)
              : rawValue,
            count,
            isChecked: false,
          });
        }
      }

      if (cleanFieldName === 'tree_nsdi') {
        if (filters.length > 0) {
          const hierarchyView = buildTreeNsdiHierarchyView(
            filters,
            facetConfig.hierarchyTreeBase,
          );

          const sectionCount = hierarchyView.filters.reduce(
            (total, item) => total + (item.count || 0),
            0,
          );
          const hierarchyTitle = hierarchyView.matchedHierarchyPath
            ?.split('/')
            .filter(Boolean)
            .at(-1);
          const title = hierarchyView.matchedHierarchyPath
            ? `${configuredDisplayNames[cleanFieldName] || cleanFieldName}: ${
                hierarchyTitle || hierarchyView.matchedHierarchyPath
              }`
            : configuredDisplayNames[cleanFieldName] || cleanFieldName;

          sections.push({
            id: cleanFieldName,
            title,
            isExpanded: false,
            count: sectionCount,
            ...(configuredCategoryPaths[cleanFieldName]?.length
              ? { categoryPath: configuredCategoryPaths[cleanFieldName] }
              : {}),
            ...(typeof configuredMaxValues[cleanFieldName] === 'number'
              ? { maxValues: configuredMaxValues[cleanFieldName] }
              : {}),
            valueCategories: hierarchyView.valueCategories,
            ...(hierarchyView.emptyMessage
              ? { emptyMessage: hierarchyView.emptyMessage }
              : {}),
            ...(hierarchyView.ungroupedFilters?.length
              ? { ungroupedFilters: hierarchyView.ungroupedFilters }
              : {}),
            filters: hierarchyView.filters,
          });
        }
        return;
      }

      const sectionCount = filters.reduce(
        (total, item) => total + (item.count || 0),
        0,
      );

      const { groupedValues, ungroupedFilters } = groupFacetValues(
        filters,
        configuredValueCategories[cleanFieldName],
      );

      if (filters.length > 0) {
        sections.push({
          id: cleanFieldName,
          title: configuredDisplayNames[cleanFieldName] || cleanFieldName,
          isExpanded: false,
          count: sectionCount,
          ...(configuredCategoryPaths[cleanFieldName]?.length
            ? { categoryPath: configuredCategoryPaths[cleanFieldName] }
            : {}),
          ...(typeof configuredMaxValues[cleanFieldName] === 'number'
            ? { maxValues: configuredMaxValues[cleanFieldName] }
            : {}),
          ...(groupedValues?.length ? { valueCategories: groupedValues } : {}),
          ...(ungroupedFilters?.length ? { ungroupedFilters } : {}),
          filters,
        });
      }
    });

    return sections;
  }
}

// Export convenience functions
export const voyagerSearch = VoyagerSearchService.search;
export const voyagerFetchFacets = VoyagerSearchService.fetchFacets;
export const voyagerFetchHeatmap = VoyagerSearchService.fetchHeatmap;
export const voyagerFetchDisplayConfig =
  VoyagerSearchService.fetchDisplayConfig;
export const voyagerFetchItemDetails = VoyagerSearchService.fetchItemDetails;
export const voyagerSubmitVote = VoyagerSearchService.submitVote;
